/// <reference path="../../../models/tableColumn.ts" />
import { AbstractDialogContent } from '@abstracts/dialog/dialog-content';
import { Component, Injector, Input, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ChangePasswordRequest } from '@dto/changePasswordRequest';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { PasswordConfirmationValidatorService } from '@services/custom-validators/password-confirm-validator.service';
import { UsersService } from '@services/entity/users.service';
import { FormService } from '@services/form/form.service';
import { ChangePasswordSuccessContentComponent } from '@components/dialog/change-password-success-dialog-content/change-password-success-dialog-content.component';

@Component({
  selector: 'app-change-password-dialog-content',
  templateUrl: './change-password-dialog-content.component.html',
  styleUrls: ['./change-password-dialog-content.component.scss'],
  providers: [FormService]
})
export class ChangePasswordDialogContentComponent extends AbstractDialogContent implements OnInit {

  public className = 'ChangePasswordDialogContentComponent';

  public form: UntypedFormGroup;

  public passwordHasUpperCase: boolean = false
  public passwordHasLowerCase: boolean = false;
  public passwordHasDigit: boolean = false
  public passwordHasLength: boolean = false
  public passwordHasSpecialCharacter: boolean = false
  public passwordQuality: number = 0
  public passwordIsMatching: boolean = false
  public passwordRating: string = ''
  public passwordRatings: Array<string> = [
    '', 'Sehr Schwach', 'Schwach', 'Ausreichend', 'Gut', 'Stark', 'Sehr Stark', 'Super Stark', 'Stark wie Krupp Stahl'
  ]

  constructor(
    public activeModal: NgbActiveModal,
    public formService: FormService,
    private usersService: UsersService,
    private dialog: NgbModal,
    private passwordConfirmValidator: PasswordConfirmationValidatorService,
    injector: Injector) {
    super(activeModal, injector);
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.formService.validators = {
      currentPassword: [Validators.required],
      newPassword: [Validators.required, Validators.pattern(/(?=.*[A-Z])(?=.*[a-z])(?=.*\d)/g), Validators.minLength(10)],
      newPasswordConfirm: [Validators.required, Validators.pattern(/(?=.*[A-Z])(?=.*[a-z])(?=.*\d)/g), Validators.minLength(10)],
    }

    this.formService.form = new UntypedFormGroup({
      currentPassword: new UntypedFormControl('', this.formService.validators['currentPassword']),
      newPassword: new UntypedFormControl('', this.formService.validators['newPassword']),
      newPasswordConfirm: new UntypedFormControl('', this.formService.validators['newPasswordConfirm']),
    });

    this.formService.form.get('newPasswordConfirm').setValidators([Validators.required,
      this.passwordConfirmValidator.validateConfirmPassword(this.formService.form.get('newPassword'))]);
  }

  onSubmit(formValue) {
    const changePasswordRequest: ChangePasswordRequest = {
      currentPassword: formValue.currentPassword,
      newPassword: formValue.newPassword
    }

    this.usersService.changePasswordOfCurrentUser(changePasswordRequest).subscribe(result => {
      if (result.isSuccess) {
        this.openChangePasswordSuccessDialog();
      }
    }, error => {
      this.messageService.setError(error);
    });
  }

  openChangePasswordSuccessDialog() {
    const dialogRef = this.dialog.open(ChangePasswordSuccessContentComponent, { centered: true, backdrop: 'static' });
    this.activeModal.close();
  }

  checkPassword(p) {
    // This regex will enforce these rules:
    // At least one upper case English letter,  (?=.*?[A-Z])
    // At least one lower case English letter,  (?=.*?[a-z])
    // At least one digit,                      (?=.*?[0-9])
    // At least one special character,          (?=.*?[#?!@$%^&*-])
    // Minimum ten in length.                   {10,}

    // let requirements = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d){10,}$/

    this.passwordQuality = 0

    let upperCase = /^(?=.*[A-Z])/g
    this.passwordHasUpperCase = upperCase.test(p)
    if (upperCase.test(p) && p.match(upperCase) && p.length > this.passwordQuality * 2) this.passwordQuality += 1

    let lowerCase = /^(?=.*[a-z])/g
    this.passwordHasLowerCase = lowerCase.test(p)
    if (lowerCase.test(p) && p.match(lowerCase) && p.length > this.passwordQuality * 2) this.passwordQuality += 1

    let digit = /^(?=.*\d)/g
    this.passwordHasDigit = digit.test(p)
    if (digit.test(p) && p.match(digit) && p.length > this.passwordQuality * 2) this.passwordQuality += 1

    this.passwordHasLength = p.length >= 10
    this.passwordQuality += parseInt(Math.abs(p.length / 10).toFixed(0))

    let specialCharacter = /^(?=.*?[#?!@$%^&*-])/
    this.passwordHasSpecialCharacter = specialCharacter.test(p)
    if (specialCharacter.test(p)) this.passwordQuality += 1

    if (this.passwordQuality > this.passwordRatings.length - 1) {
      this.passwordRating = this.passwordRatings[this.passwordRatings.length]
    } else {
      this.passwordRating = this.passwordRatings[this.passwordQuality]
    }

    this.passwordIsMatching = this.formService.form.get('password').value == this.formService.form.get('confirm').value
  }
}
